import { Box, Button, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Text, Flex, Spacer, Progress } from "@chakra-ui/react";
import { FC, useEffect, useRef, useState } from "react";
import Dropzone from "react-dropzone";
import 'react-image-crop/dist/ReactCrop.css';
import SizeUtils from "../../utils/size-utils";
import { v4 as uuidv4 } from 'uuid';
import LogosNotification from "../../utils/notification-utils";
import moment from "moment";

interface UploadVideoDialogProps {
    title: string
    onClose: any,
    handleUploadChunk: any,
    handleUploadComplete: any
};


const chunkSize = 1048576 * 3;//its 3MB, increase the number measure in mb

const UploadVideoDialog: FC<UploadVideoDialogProps> = ({ title, onClose, handleUploadChunk, handleUploadComplete }) => {
    const [videoSrc, setVideoSrc] = useState<any>(null);
    const [startUpload, setStartUpload] = useState<boolean>(false);
    const [isSubmiting, setIsSubmitting] = useState<boolean>(false);
    const [showProgress, setShowProgress] = useState<boolean>(false);
    const [counter, setCounter] = useState<number>(1);
    const [beginingOfTheChunk, setBeginingOfTheChunk] = useState<number>(0);
    const [endOfTheChunk, setEndOfTheChunk] = useState<number>(chunkSize);
    const [progress, setProgress] = useState<number>(0);
    const [fileGuid, setFileGuid] = useState<string>("");
    const [fileSize, setFileSize] = useState<number>(0);
    const [chunkCount, setChunkCount] = useState<number>(0);

    useEffect(() => {
        if (fileSize > 0) {
            fileUpload();
        }
    }, [startUpload, progress]);

    const fileUpload = () => {
        setCounter(counter + 1);
        if (counter <= chunkCount) {
            var chunk = videoSrc.slice(beginingOfTheChunk, endOfTheChunk);
            uploadChunk(chunk)
        }
    }

    const uploadChunk = async (chunk: any) => {
        handleUploadChunk(chunk, counter, fileGuid).then((response: any) => {
            setBeginingOfTheChunk(endOfTheChunk);
            setEndOfTheChunk(endOfTheChunk + chunkSize);
            if (counter == chunkCount) {
                console.log('Process is complete, counter', counter)
                uploadCompleted();
            } else {
                var percentage = (counter / chunkCount) * 100;
                setProgress(percentage);
            }
        }).catch(() => {
            setIsSubmitting(false);
            alert("Error Upload Video");
        });
    }

    const uploadCompleted = () => {
        handleUploadComplete(fileGuid, videoSrc.name, videoSrc.size).then((response: any) => {
            setProgress(100);
            setTimeout(() => {
                setIsSubmitting(false);
                onClose();
                window.location.reload();
            }, 1000);
        }).catch(() => {
            setIsSubmitting(false);
            alert("Error Upload Video");
        });
    }

    const resetChunkProperties = () => {
        setShowProgress(true)
        setProgress(0)
        setCounter(1)
        setBeginingOfTheChunk(0)
        setEndOfTheChunk(chunkSize)
    }

    const handleDrop = (acceptedFiles: any) => {
        if (acceptedFiles.length > 0) {
            setVideoSrc(acceptedFiles[0]);
        }
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);
        resetChunkProperties();
        setFileSize(videoSrc.size)

        const _totalCount = videoSrc.size % chunkSize == 0 ? videoSrc.size / chunkSize : Math.floor(videoSrc.size / chunkSize) + 1; // Total count of chunks will have been upload to finish the file
        setChunkCount(_totalCount)

        setVideoSrc(videoSrc);
        const _fileID = uuidv4() + "." + videoSrc.name.split('.').pop();
        setFileGuid(_fileID);

        setStartUpload(true);
    };

    return (
        <Modal size={'lg'} isOpen={true} onClose={isSubmiting ? () => { } : onClose} trapFocus={false} >
            <ModalOverlay />
            <ModalContent>
                <ModalHeader textAlign={"center"}>{title}</ModalHeader>
                {
                    !isSubmiting &&
                    <ModalCloseButton />
                }
                <ModalBody pt={5}>
                    {
                        !Boolean(videoSrc) &&
                        <>
                            <Text>File type: MP4</Text>
                            <Dropzone
                                onDrop={handleDrop}
                                accept={{ 'video/mp4': ['.mp4'] }}
                            >
                                {({ getRootProps, getInputProps }) => (
                                    <Box
                                        my={4}
                                        {...getRootProps({ className: "dropzone" })}
                                        rounded={"lg"}
                                        border="2px"
                                        borderStyle={"dashed"}
                                        borderColor={"primary.500"}
                                        box-sizing="border-box"
                                    >
                                        <Flex height={'sm'}>
                                            <Spacer />
                                            <Box as={Flex} justifyItems={"center"} alignItems={'center'}>
                                                <Box>
                                                    <Text textAlign={'center'} fontWeight={'bold'} fontSize={'xl'}>
                                                        Drag & Drop
                                                    </Text>
                                                    <Text textAlign={'center'} mt={2}>
                                                        or
                                                    </Text>
                                                    <Button mt={2} variant={'ghost'}>
                                                        Select an video
                                                        <input {...getInputProps()} />
                                                    </Button>
                                                </Box>
                                            </Box>
                                            <Spacer />
                                        </Flex>
                                    </Box>
                                )}
                            </Dropzone>
                        </>
                    }

                    {
                        Boolean(videoSrc) &&
                        <Box my={10}>
                            <Text>Selected Video: <Text as={'span'} fontWeight={'bold'}>{videoSrc.name}</Text></Text>
                                <Text>Size Video: <Text as={'span'} fontWeight={'bold'}>{SizeUtils.toStringByte(videoSrc.size)}</Text></Text>
                        </Box>
                    }
                    {
                        isSubmiting &&
                        <Box mt={2}>
                            <Text mb={1} fontSize={'sm'}>Uploading: <Text as={'span'} fontWeight={'bold'}> {progress.toFixed(0)}%</Text></Text>
                            <Progress hasStripe value={progress} />
                        </Box>
                    }
                </ModalBody>
                {
                    Boolean(videoSrc) &&
                    <ModalFooter>
                        {
                            !isSubmiting &&
                            <Button variant={'outline'} mr={3} onClick={onClose}>
                                Close
                            </Button>
                        }
                        <Button onClick={handleSubmit} isLoading={isSubmiting}>Upload</Button>
                    </ModalFooter>
                }
            </ModalContent>
        </Modal >
    );
};

export default UploadVideoDialog;